import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  Divider,
  useTheme,
  useMediaQuery,
  LinearProgress,
  Breadcrumbs,
  Link,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Warning as WarningIcon,
  AccountBalance as AccountBalanceIcon,
  Person as PersonIcon,
  Description as DescriptionIcon,
  CalendarToday as CalendarTodayIcon,
  AttachMoney as AttachMoneyIcon,
  TrendingUp as TrendingUpIcon,
  Home as HomeIcon,
  NavigateNext as NavigateNextIcon,
  LocationOn as LocationOnIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import api from '../../services/api';

const CustomerLoanSchedule = () => {
  const { customerId, loanId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const toast = useToast();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [loans, setLoans] = useState([]);
  const [installments, setInstallments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterLoanId, setFilterLoanId] = useState('');
  const [filterNIC, setFilterNIC] = useState('');
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);
  const [closeReason, setCloseReason] = useState('');
  const [closingLoan, setClosingLoan] = useState(false);
  const [expandedAreas, setExpandedAreas] = useState(new Set());

  useEffect(() => {
    if (customerId && loanId) {
      setSelectedLoan(null);
      setInstallments([]);
      fetchLoanSchedule(customerId, loanId);
    } else if (customerId) {
      setSelectedLoan(null);
      setInstallments([]);
      setSelectedCustomer(null);
      setLoans([]);
      fetchCustomerLoans(customerId);
    } else {
      setSelectedCustomer(null);
      setSelectedLoan(null);
      setLoans([]);
      setInstallments([]);
      fetchCustomers();
    }
  }, [customerId, loanId]);

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filterLoanId) {
        const loanIdNum = parseInt(filterLoanId.trim());
        if (!isNaN(loanIdNum) && loanIdNum > 0) {
          params.append('loanId', loanIdNum);
        }
      }
      if (filterNIC) params.append('nic', filterNIC);
      
      const url = params.toString() ? `/admin/customers?${params.toString()}` : '/admin/customers';
      const response = await api.get(url);
      setCustomers(response.data || []);
    } catch (error) {
      console.error('Error fetching customers:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Network Error';
      toast.error('Error fetching customers: ' + errorMessage);
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomerLoans = async (id) => {
    setLoading(true);
    try {
      const response = await api.get(`/admin/customers/${id}/loans`);
      setSelectedCustomer(response.data.customer);
      setLoans(response.data.loans || []);
    } catch (error) {
      console.error('Error fetching customer loans:', error);
      toast.error('Error fetching customer loans');
    } finally {
      setLoading(false);
    }
  };

  const fetchLoanSchedule = async (custId, lnId) => {
    setLoading(true);
    try {
      const response = await api.get(`/admin/customers/${custId}/loans/${lnId}/schedule`);
      setSelectedCustomer(response.data.customer);
      setSelectedLoan(response.data.loan);
      setInstallments(response.data.installments || []);
    } catch (error) {
      console.error('Error fetching loan schedule:', error);
      toast.error('Error fetching loan schedule');
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = async () => {
    if (!customerId && !loanId) {
      await fetchCustomers();
    } else {
      navigate('/admin/customer-loan-schedule');
    }
  };

  const handleClearFilter = async () => {
    setFilterLoanId('');
    setFilterNIC('');
    if (!customerId && !loanId) {
      await fetchCustomers();
    } else {
      navigate('/admin/customer-loan-schedule');
    }
  };

  const handleCustomerSelect = (customer) => {
    navigate(`/admin/customer-loan-schedule/${customer.id}`);
  };

  const handleLoanSelect = (loan) => {
    navigate(`/admin/customer-loan-schedule/${selectedCustomer.id}/loans/${loan.id}`);
  };

  const handleCloseLoanClick = () => {
    setCloseDialogOpen(true);
    setCloseReason('');
  };

  const handleCloseLoan = async () => {
    if (!selectedLoan || !user) {
      toast.error('Error: Loan or user information missing');
      return;
    }

    setClosingLoan(true);
    try {
      const params = { adminId: user.userId };
      if (closeReason.trim()) {
        params.reason = closeReason.trim();
      }

      await api.post(`/admin/loans/${selectedLoan.id}/close`, null, { params });
      toast.success('Loan closed successfully. Arrears have been tracked separately.');
      setCloseDialogOpen(false);
      setCloseReason('');
      
      await fetchLoanSchedule(customerId, loanId);
    } catch (error) {
      console.error('Error closing loan:', error);
      toast.error('Error closing loan: ' + (error.response?.data?.error || error.message));
    } finally {
      setClosingLoan(false);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'Rs 0.00';
    return new Intl.NumberFormat('en-LK', {
      style: 'currency',
      currency: 'LKR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-LK', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-LK', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const calculateStatistics = () => {
    if (!installments || installments.length === 0) {
      return {
        totalInstallments: 0,
        paidInstallments: 0,
        pendingInstallments: 0,
        overdueInstallments: 0,
        totalDue: 0,
        totalPaid: 0,
        totalBalance: 0,
        totalArrears: 0,
      };
    }

    const filteredInstallments = installments.filter(inst => inst.installmentNumber > 1);
    const totalInstallments = filteredInstallments.length;
    const paidInstallments = filteredInstallments.filter(inst => inst.status === 'PAID').length;
    const pendingInstallments = filteredInstallments.filter(inst => inst.status === 'PENDING').length;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const overdueInstallments = filteredInstallments.filter(inst => {
      if (inst.status === 'PAID') return false;
      const dueDate = new Date(inst.dueDate);
      dueDate.setHours(0, 0, 0, 0);
      return dueDate < today;
    }).length;

    const totalDue = filteredInstallments.reduce((sum, inst) => sum + parseFloat(inst.totalAmount || 0), 0);
    const totalPaid = filteredInstallments.reduce((sum, inst) => sum + parseFloat(inst.paidAmount || 0), 0);
    const totalBalance = totalDue - totalPaid;
    
    let totalArrears = 0;
    filteredInstallments.forEach(inst => {
        const totalDue = parseFloat(inst.totalAmount || 0);
        const paid = parseFloat(inst.paidAmount || 0);
        const balance = totalDue - paid;
        if (balance > 0) {
        totalArrears += balance;
        }
      });
    
    return {
      totalInstallments,
      paidInstallments,
      pendingInstallments,
      overdueInstallments,
      totalDue,
      totalPaid,
      totalBalance,
      totalArrears,
    };
  };

  const stats = calculateStatistics();
  const paymentProgress = stats.totalInstallments > 0 
    ? (stats.paidInstallments / stats.totalInstallments) * 100 
    : 0;

  const getStatusColor = (status) => {
    switch (status) {
      case 'PAID':
        return 'success';
      case 'PENDING':
        return 'warning';
      case 'OVERDUE':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'PAID':
        return <CheckCircleIcon fontSize="small" />;
      case 'PENDING':
        return <PendingIcon fontSize="small" />;
      case 'OVERDUE':
        return <WarningIcon fontSize="small" />;
      default:
        return null;
    }
  };

  const isOverdue = (installment) => {
    if (installment.status === 'PAID') return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dueDate = new Date(installment.dueDate);
    dueDate.setHours(0, 0, 0, 0);
    return dueDate < today;
  };

  if (loading && !selectedLoan) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  // Loan Schedule View
  if (selectedLoan && selectedCustomer && loanId) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: '100%', overflowX: 'hidden' }}>
        {/* Breadcrumbs */}
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 3 }}>
          <Link
            component="button"
            variant="body1"
            onClick={() => navigate('/admin/customer-loan-schedule')}
            sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'primary.main' }}
          >
            <HomeIcon sx={{ mr: 0.5, fontSize: 20 }} />
            Customers
          </Link>
          <Link
            component="button"
            variant="body1"
            onClick={() => navigate(`/admin/customer-loan-schedule/${selectedCustomer.id}`)}
            sx={{ textDecoration: 'none', color: 'primary.main' }}
          >
            {selectedCustomer.fullName || selectedCustomer.name}
          </Link>
          <Typography color="text.primary">Loan #{selectedLoan.id}</Typography>
        </Breadcrumbs>

        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 2 }}>
      <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#0D47A1', mb: 1 }}>
              Loan Schedule
            </Typography>
            <Typography variant="body1" color="textSecondary">
              Complete payment history and installment details
            </Typography>
          </Box>
        <Button 
            variant="outlined"
          startIcon={<ArrowBackIcon />} 
            onClick={() => navigate(`/admin/customer-loan-schedule/${selectedCustomer.id}`)}
        >
            Back to Loans
        </Button>
        </Box>

        {/* Statistics Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: 'linear-gradient(135deg, #0D47A1 0%, #1565C0 100%)', color: 'white' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                      {stats.totalInstallments}
        </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Total Installments
                    </Typography>
                  </Box>
                  <DescriptionIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: 'linear-gradient(135deg, #2E7D32 0%, #1B5E20 100%)', color: 'white' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                      {stats.paidInstallments}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Paid Installments
                    </Typography>
                  </Box>
                  <CheckCircleIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: 'linear-gradient(135deg, #FF9800 0%, #F57C00 100%)', color: 'white' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                      {stats.pendingInstallments}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Pending Installments
                    </Typography>
                  </Box>
                  <PendingIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: 'linear-gradient(135deg, #D32F2F 0%, #B71C1C 100%)', color: 'white' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                      {stats.overdueInstallments}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Overdue Installments
                    </Typography>
                  </Box>
                  <WarningIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Payment Progress */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Payment Progress
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                {paymentProgress.toFixed(1)}%
              </Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={paymentProgress} 
              sx={{ height: 10, borderRadius: 5 }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
              <Typography variant="body2" color="textSecondary">
                {stats.paidInstallments} of {stats.totalInstallments} installments paid
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {stats.totalInstallments - stats.paidInstallments} remaining
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* Customer and Loan Details */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <PersonIcon sx={{ fontSize: 28, color: 'primary.main', mr: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Customer Details
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="textSecondary">Full Name</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {selectedCustomer.fullName || selectedCustomer.name}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="textSecondary">NIC</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {selectedCustomer.nic}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="textSecondary">Phone Number</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {selectedCustomer.phoneNumber || selectedCustomer.phone || 'N/A'}
                    </Typography>
                  </Grid>
                  {selectedCustomer.area && (
                    <Grid item xs={12}>
                      <Typography variant="body2" color="textSecondary">Area</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {selectedCustomer.area.name}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <AccountBalanceIcon sx={{ fontSize: 28, color: 'primary.main', mr: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Loan Details
                </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="textSecondary">Loan ID</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      #{selectedLoan.id}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="textSecondary">Status</Typography>
                    <Chip
                      label={selectedLoan.status}
                      color={selectedLoan.status === 'ACTIVE' ? 'success' : selectedLoan.status === 'CLOSED' ? 'default' : 'warning'}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="textSecondary">Loan Amount</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: 'primary.main' }}>
                      {formatCurrency(selectedLoan.approvedAmount || selectedLoan.requestedAmount)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="textSecondary">Document Charges</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {formatCurrency(selectedLoan.documentCharges)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ p: 1.5, bgcolor: 'primary.light', borderRadius: 1, mt: 1 }}>
                      <Typography variant="body2" color="textSecondary">Bank Deposit</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.contrastText' }}>
                        {formatCurrency(selectedLoan.bankDeposit)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="textSecondary">Disbursement Date</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {selectedLoan.disbursementDate ? formatDate(selectedLoan.disbursementDate) : 'N/A'}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Template Details */}
        {selectedLoan.loanTemplate && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <DescriptionIcon sx={{ fontSize: 28, color: 'primary.main', mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Template Details
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" color="textSecondary">Template Name</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {selectedLoan.loanTemplate.name}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" color="textSecondary">Total Installments</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {selectedLoan.loanTemplate.numberOfInstallments}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" color="textSecondary">First Installment (Deducted)</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: 'success.main' }}>
                    {formatCurrency(selectedLoan.loanTemplate.firstInstallment)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" color="textSecondary">Remaining Installments</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {selectedLoan.loanTemplate.numberOfInstallments - 1} × {formatCurrency(selectedLoan.loanTemplate.installmentAmount)}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Financial Summary */}
        <Card sx={{ mb: 3, background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)' }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Financial Summary
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" color="textSecondary">Total Due</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>
                    {formatCurrency(stats.totalDue)}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" color="textSecondary">Total Paid</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: 'success.main' }}>
                    {formatCurrency(stats.totalPaid)}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" color="textSecondary">Total Balance</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: stats.totalBalance > 0 ? 'error.main' : 'success.main' }}>
                    {formatCurrency(stats.totalBalance)}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" color="textSecondary">Total Arrears</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: 'error.main' }}>
                    {formatCurrency(stats.totalArrears)}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Close Loan Section */}
        {selectedLoan.status !== 'CLOSED' && (
          <Card sx={{ mb: 3, bgcolor: 'warning.light', border: '2px solid', borderColor: 'warning.main' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                    Close Loan
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Total Arrears: <strong>{formatCurrency(stats.totalArrears)}</strong>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Closing this loan will mark all unpaid installments and track arrears separately.
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<CloseIcon />}
                  onClick={handleCloseLoanClick}
                  disabled={closingLoan}
                  size="large"
                >
                  Close Loan
                </Button>
              </Box>
            </CardContent>
          </Card>
        )}

        {selectedLoan.status === 'CLOSED' && selectedLoan.closedByAdmin && (
          <Card sx={{ mb: 3, bgcolor: 'info.light' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <CheckCircleIcon sx={{ color: 'info.main' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Loan Closed
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                This loan was closed by {selectedLoan.closedByAdminUser?.fullName || 'Admin'}
                {selectedLoan.closedAt && ` on ${formatDateTime(selectedLoan.closedAt)}`}
              {selectedLoan.arrearsAmount && selectedLoan.arrearsAmount > 0 && 
                  `. Arrears deducted: ${formatCurrency(selectedLoan.arrearsAmount)}`}
            </Typography>
            </CardContent>
          </Card>
        )}

        {/* Installments Table */}
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Installment Schedule
            </Typography>
            <TableContainer sx={{ maxHeight: 600, overflowX: 'auto' }}>
              <Table stickyHeader>
            <TableHead>
              <TableRow>
                    <TableCell sx={{ fontWeight: 700, bgcolor: 'primary.main', color: 'white' }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: 'primary.main', color: 'white' }}>Due Date</TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: 'primary.main', color: 'white' }} align="right">Base Amount</TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: 'primary.main', color: 'white' }} align="right">Late Interest</TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: 'primary.main', color: 'white' }} align="right">Carryover</TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: 'primary.main', color: 'white' }} align="right">Advance</TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: 'primary.main', color: 'white' }} align="right">Total Due</TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: 'primary.main', color: 'white' }} align="right">Paid</TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: 'primary.main', color: 'white' }} align="right">Balance</TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: 'primary.main', color: 'white' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: 'primary.main', color: 'white' }}>Paid Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {installments
                .filter(installment => installment.installmentNumber > 1)
                .map((installment) => {
                  const totalDue = parseFloat(installment.totalAmount || 0);
                  const paid = parseFloat(installment.paidAmount || 0);
                  const balance = totalDue - paid;
                      const overdue = isOverdue(installment);
                  
                  return (
                        <TableRow 
                          key={installment.id}
                          sx={{
                            '&:hover': { bgcolor: 'action.hover' },
                            bgcolor: overdue ? 'error.light' : installment.status === 'PAID' ? 'success.light' : 'inherit',
                          }}
                        >
                          <TableCell sx={{ fontWeight: 600 }}>{installment.installmentNumber}</TableCell>
                      <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <CalendarTodayIcon fontSize="small" color="action" />
                              {formatDate(installment.dueDate)}
                            </Box>
                          </TableCell>
                          <TableCell align="right">{formatCurrency(installment.amount)}</TableCell>
                          <TableCell align="right">
                            {parseFloat(installment.lateInterest || 0) > 0 ? (
                              <Typography variant="body2" color="error.main" sx={{ fontWeight: 600 }}>
                                {formatCurrency(installment.lateInterest)}
                          </Typography>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                          <TableCell align="right">
                            {parseFloat(installment.carryoverAmount || 0) > 0 ? (
                              <Typography variant="body2" color="warning.main" sx={{ fontWeight: 600 }}>
                                {formatCurrency(installment.carryoverAmount)}
                          </Typography>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                          <TableCell align="right">
                            {parseFloat(installment.advanceAmount || 0) > 0 ? (
                              <Typography variant="body2" color="info.main" sx={{ fontWeight: 600 }}>
                                {formatCurrency(installment.advanceAmount)}
                        </Typography>
                            ) : (
                              '-'
                            )}
                      </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              {formatCurrency(totalDue)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>
                              {formatCurrency(paid)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                        <Typography 
                          variant="body2" 
                              sx={{ fontWeight: 600 }}
                          color={balance === 0 ? 'success.main' : balance > 0 ? 'error.main' : 'info.main'}
                        >
                              {balance === 0 ? formatCurrency(0) : balance > 0 ? formatCurrency(balance) : `${formatCurrency(Math.abs(balance))} (Extra)`}
                        </Typography>
                      </TableCell>
                          <TableCell>
                            <Chip
                              icon={getStatusIcon(installment.status)}
                              label={installment.status}
                              color={getStatusColor(installment.status)}
                              size="small"
                              sx={{ fontWeight: 600 }}
                            />
                          </TableCell>
                          <TableCell>
                            {installment.paidDate ? (
                              <Box>
                                <Typography variant="body2">{formatDate(installment.paidDate)}</Typography>
                                {installment.paidTime && (
                                  <Typography variant="caption" color="textSecondary">
                                    {new Date(installment.paidTime).toLocaleTimeString('en-LK', { hour: '2-digit', minute: '2-digit' })}
                                  </Typography>
                                )}
                              </Box>
                            ) : (
                              '-'
                            )}
                          </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
          </CardContent>
        </Card>

        {/* Close Loan Dialog */}
        <Dialog open={closeDialogOpen} onClose={() => setCloseDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <WarningIcon color="error" />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Close Loan
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              Are you sure you want to close this loan? This action will:
              <Box component="ul" sx={{ mt: 1, pl: 2 }}>
                <li>Mark all unpaid installments as paid</li>
                <li>Track arrears amount: <strong>{formatCurrency(stats.totalArrears)}</strong> (will be shown separately, NOT deducted from capital)</li>
                <li>Close the loan permanently</li>
                <li>Notify the officer and collectors</li>
              </Box>
            </DialogContentText>
            <TextField
              fullWidth
              label="Reason for Closure (Optional)"
              multiline
              rows={3}
              value={closeReason}
              onChange={(e) => setCloseReason(e.target.value)}
              placeholder="Enter reason for closing this loan..."
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCloseDialogOpen(false)} disabled={closingLoan}>
              Cancel
            </Button>
            <Button 
              onClick={handleCloseLoan} 
              variant="contained" 
              color="error"
              disabled={closingLoan}
              startIcon={closingLoan ? <CircularProgress size={20} /> : <CloseIcon />}
            >
              {closingLoan ? 'Closing...' : 'Close Loan'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  // Customer Loans List View
  if (selectedCustomer && customerId && !loanId) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: '100%', overflowX: 'hidden' }}>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 3 }}>
          <Link
            component="button"
            variant="body1"
            onClick={() => navigate('/admin/customer-loan-schedule')}
            sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'primary.main' }}
          >
            <HomeIcon sx={{ mr: 0.5, fontSize: 20 }} />
            Customers
          </Link>
          <Typography color="text.primary">{selectedCustomer.fullName || selectedCustomer.name}</Typography>
        </Breadcrumbs>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 2 }}>
      <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#0D47A1', mb: 1 }}>
              Customer Loans
            </Typography>
            <Typography variant="body1" color="textSecondary">
              {selectedCustomer.fullName || selectedCustomer.name}
            </Typography>
          </Box>
        <Button 
            variant="outlined"
          startIcon={<ArrowBackIcon />} 
            onClick={() => navigate('/admin/customer-loan-schedule')}
        >
          Back to Customers
        </Button>
        </Box>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="textSecondary">NIC</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {selectedCustomer.nic}
        </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="textSecondary">Phone</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {selectedCustomer.phoneNumber || selectedCustomer.phone || 'N/A'}
                </Typography>
              </Grid>
              {selectedCustomer.area && (
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" color="textSecondary">Area</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {selectedCustomer.area.name}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : loans.length === 0 ? (
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 4 }}>
              <DescriptionIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="textSecondary">
                No loans found for this customer
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'primary.main' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 700 }}>Loan ID</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 700 }} align="right">Loan Amount</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 700 }}>Status</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 700 }}>Disbursement Date</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 700 }} align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loans.map((loan) => (
                  <TableRow key={loan.id} hover>
                    <TableCell sx={{ fontWeight: 600 }}>#{loan.id}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, color: 'primary.main' }}>
                      {formatCurrency(loan.approvedAmount || loan.requestedAmount)}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={loan.status}
                        color={loan.status === 'ACTIVE' ? 'success' : loan.status === 'CLOSED' ? 'default' : 'warning'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{loan.disbursementDate ? formatDate(loan.disbursementDate) : 'N/A'}</TableCell>
                    <TableCell align="center">
                      <Button 
                        variant="contained" 
                        size="small"
                        onClick={() => handleLoanSelect(loan)}
                        startIcon={<DescriptionIcon />}
                      >
                        View Schedule
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    );
  }

  // Customer List View
  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: '100%', overflowX: 'hidden' }}>
      <Typography variant="h4" sx={{ fontWeight: 700, color: '#0D47A1', mb: 3 }}>
        Customer Loan Schedules
      </Typography>
      
      {/* Filter Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <SearchIcon sx={{ fontSize: 28, color: 'primary.main', mr: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Search Customers
        </Typography>
          </Box>
          <Divider sx={{ mb: 2 }} />
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Loan ID"
              type="number"
              value={filterLoanId}
              onChange={(e) => setFilterLoanId(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleFilter();
                }
              }}
              placeholder="Enter loan ID..."
              inputProps={{ min: 1 }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="NIC"
              value={filterNIC}
              onChange={(e) => setFilterNIC(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleFilter();
                }
              }}
              placeholder="Enter NIC..."
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box sx={{ display: 'flex', gap: 1 }}>
                <Button 
                  variant="contained" 
                  onClick={handleFilter} 
                  startIcon={<SearchIcon />}
                  fullWidth
                >
                Search
              </Button>
                <Button 
                  variant="outlined" 
                  onClick={handleClearFilter}
                  fullWidth
                >
                Clear
              </Button>
            </Box>
          </Grid>
        </Grid>
        </CardContent>
      </Card>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : customers.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <PersonIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="textSecondary" gutterBottom>
              No customers found
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Try adjusting your search filters
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Box>
          {(() => {
            // Group customers by area and calculate loan counts
            const groupedByArea = customers.reduce((acc, customer) => {
              const areaName = customer.area?.name || 'No Area Assigned';
              if (!acc[areaName]) {
                acc[areaName] = {
                  customers: [],
                  activeLoanCount: 0,
                  totalLoanCount: 0
                };
              }
              
              // Use loanCount from backend response, or calculate from loans array if available
              const loanCount = customer.loanCount !== undefined 
                ? customer.loanCount 
                : (customer.loans?.filter(loan => 
                    loan.status === 'ACTIVE' || loan.status === 'APPROVED'
                  ) || []).length;
              
              const totalLoans = customer.totalLoans !== undefined 
                ? customer.totalLoans 
                : (customer.loans?.length || 0);
              
              acc[areaName].customers.push({
                ...customer,
                loanCount: loanCount,
                totalLoans: totalLoans
              });
              acc[areaName].activeLoanCount += loanCount;
              acc[areaName].totalLoanCount += totalLoans;
              
              return acc;
            }, {});

            // Sort areas alphabetically
            const sortedAreas = Object.keys(groupedByArea).sort();

            return sortedAreas.map((areaName) => {
              const areaData = groupedByArea[areaName];
              const isExpanded = expandedAreas.has(areaName);

              return (
                <Accordion
                  key={areaName}
                  expanded={isExpanded}
                  onChange={() => {
                    const newExpanded = new Set(expandedAreas);
                    if (isExpanded) {
                      newExpanded.delete(areaName);
                    } else {
                      newExpanded.add(areaName);
                    }
                    setExpandedAreas(newExpanded);
                  }}
                  sx={{
                    mb: 2,
                    boxShadow: 3,
                    borderRadius: 2,
                    '&:before': { display: 'none' },
                    overflow: 'hidden',
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}
                    sx={{
                      background: 'linear-gradient(135deg, #1976D2 0%, #0D47A1 100%)',
                      color: 'white',
                      minHeight: 64,
                      '&.Mui-expanded': {
                        minHeight: 64,
                      },
                      '& .MuiAccordionSummary-content': {
                        alignItems: 'center',
                        my: 1,
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 2 }}>
                      <LocationOnIcon sx={{ fontSize: 32 }} />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                          {areaName}
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                          {areaData.customers.length} customer(s) • {areaData.activeLoanCount} active loan(s) • {areaData.totalLoanCount} total loan(s)
                        </Typography>
                      </Box>
                      <Chip
                        label={`${areaData.customers.length} Customers`}
                        sx={{
                          backgroundColor: 'rgba(255, 255, 255, 0.2)',
                          color: 'white',
                          fontWeight: 600,
                          fontSize: '0.875rem',
                        }}
                      />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 0 }}>
                    <TableContainer component={Paper} variant="outlined">
                      <Table>
                        <TableHead>
                          <TableRow sx={{ bgcolor: 'primary.light' }}>
                            <TableCell sx={{ fontWeight: 700 }}>ID</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Full Name</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>NIC</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Phone Number</TableCell>
                            <TableCell sx={{ fontWeight: 700 }} align="center">Active Loans</TableCell>
                            <TableCell sx={{ fontWeight: 700 }} align="center">Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {areaData.customers.map((customer) => (
                            <TableRow key={customer.id} hover>
                              <TableCell>{customer.id}</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>{customer.fullName || customer.name}</TableCell>
                              <TableCell>{customer.nic}</TableCell>
                              <TableCell>{customer.phoneNumber || customer.phone || 'N/A'}</TableCell>
                              <TableCell align="center">
                                <Chip
                                  label={customer.loanCount}
                                  color={customer.loanCount > 0 ? 'primary' : 'default'}
                                  size="small"
                                  sx={{ fontWeight: 600 }}
                                />
                              </TableCell>
                              <TableCell align="center">
                                <Button 
                                  variant="contained" 
                                  size="small"
                                  onClick={() => handleCustomerSelect(customer)}
                                  startIcon={<AccountBalanceIcon />}
                                >
                                  View Loans
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </AccordionDetails>
                </Accordion>
              );
            });
          })()}
        </Box>
      )}
    </Box>
  );
};

export default CustomerLoanSchedule;
